<template>
  <router-link :to="{ name: 'Home' }" class="p-2 bg-gray-900 flex items-center justify-center border-b border-gray-800 space-x-1 text-white hover:text-red-900 cursor-pointer duration-300" style="font-family: 'Creepster', cursive;">
    <div class="text-4xl">
      ZOMBIEKUB
    </div>
  </router-link>

  <!-- Loading State -->
  <div v-if="loading && user" class="bg-gray-900 bg-opacity-90 p-4 shadow-xl border-t border-gray-800">
    <div class="text-center text-gray-400">
      <i class="fas fa-spinner animate-spin text-2xl"></i>
      <p class="mt-2">กำลังโหลดทรัพยากร...</p>
    </div>
  </div>

  <!-- Main Content -->
  <div v-else-if="user && userData?.characterCreated" class="bg-gray-900 bg-opacity-90 p-4 shadow-xl border-b border-gray-800">
    <!-- Resources -->
    <div class="grid grid-cols-4 gap-1 sm:gap-3">
      <!-- Cash -->
      <div class="flex items-center justify-between bg-gradient-to-r from-green-900 to-gray-800 p-1 sm:p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-green-500/50 hover:scale-105">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-money-bill text-green-400 text-xs sm:text-sm"></i>
          <span class="font-bold text-xs sm:text-sm text-white">{{ userData?.cash || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">แคช</span>
      </div>

      <!-- Diamond -->
      <div class="flex items-center justify-between bg-gradient-to-r from-blue-900 to-gray-800 p-1 sm:p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-blue-500/50 hover:scale-105">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-gem text-blue-400 text-xs sm:text-sm"></i>
          <span class="font-bold text-xs sm:text-sm text-white">{{ characterData?.diamond || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">เพชร</span>
      </div>

      <!-- Gold -->
      <div class="flex items-center justify-between bg-gradient-to-r from-yellow-900 to-gray-800 p-1 sm:p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-yellow-500/50 hover:scale-105">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-g text-yellow-300 text-xs sm:text-sm"></i>
          <span class="font-bold text-xs sm:text-sm text-white">{{ characterData?.gold || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">ทอง</span>
      </div>

      <!-- Silver -->
      <div class="flex items-center justify-between bg-gradient-to-r from-gray-700 to-gray-800 p-1 sm:p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-gray-400/50 hover:scale-105">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-coins text-gray-300 text-xs sm:text-sm"></i>
          <span class="font-bold text-xs sm:text-sm text-white">{{ characterData?.silver || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">เหรียญ</span>
      </div>
    </div>

    <!-- Character Status with Notifications -->
    <div class="grid grid-cols-6 sm:grid-cols-5 gap-1 sm:gap-3 mt-1 sm:mt-2">
      <!-- HP with Health Bar -->
      <div
  class="flex items-center justify-between p-1 sm:p-2 rounded-lg shadow-md col-span-3 sm:col-span-1"
  :class="{ 'bg-red-900': hpPercentage <= 20, 'bg-gray-800': hpPercentage > 20 }"
>
  <div class="flex flex-col space-y-1 w-full">
    <div class="flex justify-between items-center space-x-3">
      <div class="flex items-center space-x-3">
        <i class="fa-solid fa-heart text-red-500"></i>
        <span
          class="font-bold text-xs sm:text-sm"
          :class="{ 'text-red-500': hpPercentage <= 20, 'text-white': hpPercentage > 20 }"
        >
          {{ playerHp }}/{{ playerMaxHp }}
        </span>
      </div>
      <div class="text-xs text-gray-400">พลังชีวิต</div>
    </div>

    <!-- Health Bar with 10% Markers -->
    <div class="relative w-full bg-gray-700 rounded-full h-2 overflow-hidden">
      <!-- Marker lines every 10% -->
      <template v-for="n in 9" :key="n">
        <div
          class="absolute top-0 bottom-0 w-0.5 bg-gray-900 opacity-50"
          :style="{ left: `${n * 10}%` }"
        ></div>
      </template>

      <!-- HP fill -->
      <div
        class="bg-red-500 h-2 rounded-full transition-all duration-300"
        :style="{ width: `${hpPercentage}%` }"
      ></div>
    </div>
  </div>
</div>


      <!-- Energy Level with Bar (Clickable) -->
      <div class="flex items-center justify-between p-1 sm:p-2 rounded-lg shadow-md col-span-3 sm:col-span-1 bg-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 border border-gray-600" @click="showEnergyModal = true">
        <div class="flex flex-col space-y-1 w-full">
          <div class="flex justify-between items-center space-x-3">
            <div class="flex items-center space-x-3">
              <i class="fa-solid fa-bolt text-yellow-400 animate-swing"></i>
              <span class="font-bold text-xs sm:text-sm text-white">{{ characterPrivateData?.energyLevel || 0 }}</span>
            </div>
            <div class="text-xs text-gray-400">พลังงาน</div>
          </div>
          <div class="w-full bg-gray-700 rounded-full h-2">
            <div class="bg-yellow-500 h-2 rounded-full" :style="{ width: `${energyPercentage}%` }"></div>
          </div>
        </div>
      </div>

      <!-- Food Level (Clickable) -->
      <div class="flex items-center justify-between p-1 sm:p-2 rounded-lg shadow-md col-span-2 sm:col-span-1 cursor-pointer transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 border border-gray-600" :class="{ 'bg-red-900': (characterPrivateData?.foodLevel || 0) <= 10, 'bg-gray-800': (characterPrivateData?.foodLevel || 0) > 10 }" @click="showFoodModal = true">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-drumstick-bite animate-swing" :class="{ 'text-red-500': (characterPrivateData?.foodLevel || 0) <= 50, 'text-gray-300': (characterPrivateData?.foodLevel || 0) > 50 }"></i>
          <span class="font-bold text-xs sm:text-sm" :class="{ 'text-red-500': (characterPrivateData?.foodLevel || 0) <= 50, 'text-white': (characterPrivateData?.foodLevel || 0) > 50 }">{{ characterPrivateData?.foodLevel || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">อาหาร</span>
      </div>

      <!-- Happiness Level (Clickable) -->
      <div class="flex items-center justify-between p-1 sm:p-2 rounded-lg shadow-md col-span-2 sm:col-span-1 cursor-pointer transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 border border-gray-600" :class="{ 'bg-red-900': (characterPrivateData?.happyLevel || 0) <= 10, 'bg-gray-800': (characterPrivateData?.happyLevel || 0) > 10 }" @click="showHappinessModal = true">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-smile animate-swing" :class="{ 'text-red-500': (characterPrivateData?.happyLevel || 0) <= 50, 'text-gray-300': (characterPrivateData?.happyLevel || 0) > 50 }"></i>
          <span class="font-bold text-xs sm:text-sm" :class="{ 'text-red-500': (characterPrivateData?.happyLevel || 0) <= 50, 'text-white': (characterPrivateData?.happyLevel || 0) > 50 }">{{ characterPrivateData?.happyLevel || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">ความสุข</span>
      </div>

      <!-- Infection Level (Clickable) -->
      <div class="flex items-center justify-between p-1 sm:p-2 rounded-lg shadow-md col-span-2 sm:col-span-1 cursor-pointer transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 border border-gray-600" :class="{ 'bg-red-900': (characterPrivateData?.infectionLevel || 0) >= 90, 'bg-gray-800': (characterPrivateData?.infectionLevel || 0) < 90 }" @click="showInfectionModal = true">
        <div class="flex items-center space-x-3">
          <i class="fa-solid fa-biohazard animate-swing" :class="{ 'text-red-500': (characterPrivateData?.infectionLevel || 0) >= 90, 'text-gray-300': (characterPrivateData?.infectionLevel || 0) < 90 }"></i>
          <span class="font-bold text-xs sm:text-sm" :class="{ 'text-red-500': (characterPrivateData?.infectionLevel || 0) >= 90, 'text-white': (characterPrivateData?.infectionLevel || 0) < 90 }">{{ characterPrivateData?.infectionLevel || 0 }}</span>
        </div>
        <span class="text-xs text-gray-400 hidden sm:block">เชื้อโรค</span>
      </div>
    </div>
  </div>

  <!-- Energy Modal Component -->
  <EnergyModal :show="showEnergyModal" @close="showEnergyModal = false" />

  <!-- Food Modal Component -->
  <FoodModal :show="showFoodModal" @close="showFoodModal = false" />

  <!-- Happiness Modal Component -->
  <HappinessModal :show="showHappinessModal" @close="showHappinessModal = false" />

  <!-- Infection Modal Component -->
  <InfectionModal :show="showInfectionModal" @close="showInfectionModal = false" />
</template>

<script>
import { computed, ref } from 'vue';
import getUser from '@/composables/getUser';
import EnergyModal from '@/components/EnergyModal.vue';
import FoodModal from '@/components/FoodModal.vue';
import HappinessModal from '@/components/HappinessModal.vue';
import InfectionModal from '@/components/InfectionModal.vue';

export default {
  name: 'Header',
  components: {
    EnergyModal,
    FoodModal,
    HappinessModal,
    InfectionModal,
  },
  props: {
    userData: {
      type: Object,
      default: null,
    },
    characterData: {
      type: Object,
      default: null,
    },
    characterPrivateData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const { user } = getUser();
    const showEnergyModal = ref(false);
    const showFoodModal = ref(false);
    const showHappinessModal = ref(false);
    const showInfectionModal = ref(false);

    // Calculate maxHp
    const playerMaxHp = computed(() => {
      if (!props.characterPrivateData) return 0;
      const stamina =
        (props.characterPrivateData.itemStats?.stamina || 0) +
        (props.characterPrivateData.bonusStats?.stamina || 0) +
        (props.characterPrivateData.stats?.stamina || 0) +
        (props.characterPrivateData.traitStats?.stamina || 0) +
        (props.characterPrivateData.skillStats?.stamina || 0) +
        (props.characterPrivateData.titleStats?.stamina || 0) +
        (props.characterPrivateData.tattooStats?.stamina || 0) +
        (props.characterPrivateData.ghostStats?.stamina || 0);
      return stamina * 20 +
        (props.characterPrivateData.itemStats?.maxHp || 0) +
        (props.characterPrivateData.bonusStats?.maxHp || 0) +
        (props.characterPrivateData.stats?.maxHp || 0) +
        (props.characterPrivateData.traitStats?.maxHp || 0) +
        (props.characterPrivateData.skillStats?.maxHp || 0) +
        (props.characterPrivateData.titleStats?.maxHp || 0) +
        (props.characterPrivateData.tattooStats?.maxHp || 0) +
        (props.characterPrivateData.ghostStats?.maxHp || 0);
    });

    // Calculate playerHp
    const playerHp = computed(() => {
      if (!props.characterPrivateData) return 0;
      return Math.max(0, playerMaxHp.value - (props.characterPrivateData.damageTaken || 0));
    });

    // Calculate HP percentage
    const hpPercentage = computed(() =>
      playerMaxHp.value > 0 ? Math.min((playerHp.value / playerMaxHp.value) * 100, 100) : 0
    );

    // Calculate energy percentage (maxEnergy = 4800)
    const energyPercentage = computed(() =>
      Math.min(((props.characterPrivateData?.energyLevel || 0) / 4800) * 100, 100)
    );

    return {
      user,
      playerHp,
      playerMaxHp,
      hpPercentage,
      energyPercentage,
      showEnergyModal,
      showFoodModal,
      showHappinessModal,
      showInfectionModal,
    };
  },
};
</script>

<style scoped>
.zombie-font {
  font-family: 'Creepster', cursive;
}

.animate-swing {
  animation: swing 1s infinite;
}



button:not(:disabled):active {
  transform: scale(0.98);
}
</style>
